import {
  DefaultOptions,
  QueryClient,
  QueryFunction
} from "@tanstack/react-query";

// Shared cache-timing constants (in milliseconds) for TanStack Query.
// Centralised here so the global client (index.tsx) and per-feature queries
// (e.g. organisation data in App.tsx) use the same named values, keeping the
// staleTime/refetchInterval relationships consistent and obvious across files.
export const FIVE_MINUTES = 5 * 60 * 1000;
export const SEVENTEEN_MINUTES = 17 * 60 * 1000;
export const THIRTY_MINUTES = 30 * 60 * 1000;

// Max age of a persisted cache snapshot. A returning user within this window
// paints from the restored cache; an older snapshot is discarded so the app
// never rehydrates stale initial-load data. Kept well above the per-query
// staleTimes/refetchIntervals so a snapshot is only dropped when genuinely old.
export const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;

// Query-key roots that gate first paint and are therefore worth persisting:
// the venue list (useGetVenues), the selected venue's config (useManageAPIVenueData)
// and organisation data (useOrganisationDataQuery). Booking/customer/payment
// queries are intentionally excluded to keep the persisted snapshot small and
// free of personal data.
export const INITIAL_LOAD_QUERY_KEYS = [
  "venues",
  "venueConfig",
  "organisationData"
] as const;

// True when a query key belongs to the initial-load set above (matched on its
// first, stable segment).
export const isInitialLoadQueryKey = (queryKey: readonly unknown[]): boolean =>
  typeof queryKey[0] === "string" &&
  (INITIAL_LOAD_QUERY_KEYS as readonly string[]).includes(queryKey[0]);

// Global TanStack Query defaults tuned for initial-load performance.
//
// staleTime (5 min): data is treated as fresh for 5 minutes, so remounting a
//   component (e.g. navigating away and back within a session) serves the
//   cached value immediately instead of triggering a network refetch. Chosen
//   as a conservative window over which venue/config/organisation data is
//   acceptably stale; queries that need tighter freshness override this
//   locally, and any mutation should invalidate the relevant query key.
// gcTime (30 min): unused/inactive query data is retained in the cache for
//   30 minutes before garbage collection, well above staleTime so cached
//   entries survive brief unmounts and are reused rather than refetched.
//   Kept comfortably above any per-query refetchInterval so entries are never
//   collected exactly when they would be reused.
export const defaultQueryOptions: DefaultOptions = {
  queries: {
    // Disabled because the per-query background refetchInterval settings
    // (venue config, venues, organisation data) already keep data current;
    // refetching on every window focus would add redundant requests without
    // improving freshness beyond those intervals.
    refetchOnWindowFocus: false,
    staleTime: FIVE_MINUTES,
    gcTime: THIRTY_MINUTES
  }
};

export const createQueryClient = (): QueryClient =>
  new QueryClient({ defaultOptions: defaultQueryOptions });

// Options for the organisation-data query. queryFn and enabled are supplied at
// the call site; the timing/keys live here so they can be shared and tested.
//
// queryKey is intentionally stable (no `status`): including `status` changed the
//   cache entry when it transitioned to "success", guaranteeing a cache miss on
//   the exact render the query became enabled. Gating stays on `enabled`.
// staleTime (17 min): matches the background refetchInterval so the cached
//   organisation data is served instantly between refreshes.
// gcTime (30 min): kept above the 17-minute refetchInterval so a cached entry
//   is never garbage-collected exactly when it would be reused.
export const organisationDataQueryOptions = {
  queryKey: ["organisationData"] as const,
  staleTime: SEVENTEEN_MINUTES,
  gcTime: THIRTY_MINUTES,
  refetchInterval: SEVENTEEN_MINUTES, // 17 minute interval to not interfere with venue config reload
  refetchIntervalInBackground: false // don't refetch when the tab is not active
};

export type OrganisationDataQueryFn = QueryFunction<
  unknown,
  typeof organisationDataQueryOptions.queryKey
>;
